import { isDefined, toError } from "@clipboard-health/util-ts";
import { setAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import { getCognitoIdTokenWithProactiveRefresh } from "@src/appV2/Auth/cognito/getCognitoIdTokenWithProactiveRefresh";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import constate from "constate";
import { useCallback, useEffect, useRef } from "react";

const BEARER_TOKEN_PREFIX = "Bearer ";
const MAX_COGNITO_ERROR_LOGS = 5;

/**
 * Hook that provides centralized authentication header values for API calls.
 * Manages the migration from Firebase to Cognito authentication based on feature flags.
 *
 * Returns values ready for the Authorization header:
 * - **Cognito** (feature flag enabled): `Bearer <token>` (OAuth 2.0 standard)
 * - **Firebase** (fallback): `<token>` (legacy format)
 *
 * Implements rate-limited logging to prevent log flooding.
 *
 * @returns An object with getAuthHeaderValue function that returns the formatted auth value
 */
function useAuthHeaderProvider() {
  const isCognitoInApiCallsEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS],
  });

  const isCognitoFailClosed = useCbhFlag(CbhFeatureFlag.COGNITO_FAIL_CLOSED, {
    defaultValue: false,
  });

  // Track what we've already logged this session to prevent log flooding
  const loggedEvents = useRef({
    tokenUnavailable: false,
    flagsPartiallyEnabled: false,
    errorCount: 0,
  });

  const cognitoAuth = getCognitoSingleton();
  const firebaseAuth = getFirebaseSingleton();

  const getAuthHeaderValue = useCallback(
    async (forceRefresh?: boolean): Promise<string | undefined> => {
      const cognitoUser = await cognitoAuth.getCurrentUserInfo();
      const firebaseUser = firebaseAuth.currentUser;
      const isCognitoUserDefined = isDefined(cognitoUser);
      const isFirebaseUserDefined = isDefined(firebaseUser);

      if (!isCognitoUserDefined && !isFirebaseUserDefined) {
        return undefined;
      }

      const shouldUseCognito = getShouldUseCognito({
        isCognitoInApiCallsEnabled,
        isCognitoUserDefined,
        isFirebaseUserDefined,
        hasCbhUserId: isDefined(cognitoUser?.cbhUserId),
      });

      if (shouldUseCognito) {
        try {
          return await generateCognitoToken(forceRefresh);
        } catch (maybeError: unknown) {
          if (loggedEvents.current.errorCount < MAX_COGNITO_ERROR_LOGS) {
            logError(APP_V2_APP_EVENTS.FAILED_TO_GET_COGNITO_TOKEN, {
              error: toError(maybeError),
              metadata: { cognitoFailClosed: isCognitoFailClosed },
            });
            loggedEvents.current.errorCount += 1;
          }

          if (isCognitoFailClosed || !firebaseAuth.currentUser) {
            throw new Error("Unable to get user token");
          }
        }
      }

      // Firebase fallback (legacy format without Bearer prefix)
      return await firebaseAuth.currentUser?.getIdToken(forceRefresh);
    },
    [isCognitoInApiCallsEnabled, isCognitoFailClosed, cognitoAuth, firebaseAuth]
  );

  useEffect(() => {
    setAuthHeaderProvider(getAuthHeaderValue);
    return () => {
      setAuthHeaderProvider(undefined);
    };
  }, [getAuthHeaderValue]);

  return { getAuthHeaderValue };
}

interface ShouldUseCognitoParams {
  isCognitoInApiCallsEnabled: boolean;
  isCognitoUserDefined: boolean;
  isFirebaseUserDefined: boolean;
  hasCbhUserId: boolean;
}

function getShouldUseCognito(params: ShouldUseCognitoParams): boolean {
  const { isCognitoInApiCallsEnabled, isCognitoUserDefined, isFirebaseUserDefined, hasCbhUserId } =
    params;

  // Use Cognito when API feature flag is enabled and Cognito user exists
  const isUserCognitoWithApiFeatureFlagEnabled = isCognitoInApiCallsEnabled && isCognitoUserDefined;
  // Fall back to Cognito when API flag is disabled but only Cognito user exists
  const isUserCognitoWithApiFeatureFlagDisabled =
    !isCognitoInApiCallsEnabled && isCognitoUserDefined && !isFirebaseUserDefined;
  // Always use Cognito for onboarding users (no cbhUserId)
  const isCognitoOnboardingUser = isCognitoUserDefined && !hasCbhUserId;

  return (
    isUserCognitoWithApiFeatureFlagEnabled ||
    isUserCognitoWithApiFeatureFlagDisabled ||
    isCognitoOnboardingUser
  );
}

async function generateCognitoToken(forceRefresh?: boolean): Promise<string | undefined> {
  const token = await getCognitoIdTokenWithProactiveRefresh(forceRefresh);
  if (token) {
    return `${BEARER_TOKEN_PREFIX}${token}`;
  }

  return undefined;
}

export const [AuthHeaderProvider, useAuthHeaderContext] = constate(useAuthHeaderProvider);
